import {
  Task,
  Update,
  child,
  defModule,
  emit,
  ignore,
  transitions,
  type ModuleUpdate,
  type TaskMessage,
  type TransitionRow,
  type UpdateResult
} from '@st/redux'
import type {
  BasicUser,
  ClientOrganizerMessageType,
  STFolderSendState,
  UpdateFolderAccess,
  FolderAccessRequest
} from '@st/sdk'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import { SdkClient, Snackbar, query } from '~/effects'
import { copyTextToClipboard } from '~/features/copy-to-clipboard'
import { isValidEmail } from '@st/util/email'
import { observeCurrentUser, observeRoute, runSdkRequest } from '~/features/module-runtime'

/** One email address shown in the To field. */
export type SendClientOrganizerRecipient =
  | { type: 'knownUser'; user: BasicUser; email: string }
  | { type: 'unknownUser'; email: string }

/** The reason this email is being sent. */
export type SendClientOrganizerTarget =
  | { type: 'clientOrganizerEmail'; messageType: ClientOrganizerMessageType }
  | { type: 'taxReturnDelivery'; contractId: string }

/** The folder and email purpose for one open dialog. */
export type SendClientOrganizerDialog =
  | 'sendEmail'
  | 'shareableLink'
  | 'requestMissingDocuments'
  | 'editBulkEmail'

type DialogTarget =
  | {
      folderId: string
      dialog: 'sendEmail'
      target: SendClientOrganizerTarget
    }
  | {
      folderId: string
      dialog: 'shareableLink' | 'editBulkEmail'
      target: {
        type: 'clientOrganizerEmail'
        messageType: 'client_organizer_invitation'
      }
    }
  | {
      folderId: string
      dialog: 'requestMissingDocuments'
      target: {
        type: 'clientOrganizerEmail'
        messageType: 'client_organizer_document_request'
      }
    }

/** What an embedding parent opens the editor with. */
export type SendClientOrganizerSession = DialogTarget & { users: BasicUser[] }

/** Everything shown in the email editor. */
type EditorState = {
  /** The persisted email, kept current by its live query. */
  sendState: STFolderSendState
  body: string
  editedRecipients: SendClientOrganizerRecipient[]
  inputValue: string
  selectedEmail: string | undefined
}

/**
 * The email editor of one open dialog: its draft, over the persisted email it
 * observes live. It runs no writes and holds no write progress. It hands each
 * write to the parent that opened it, and the parent's write status for the
 * draft decides what the dialog shows (see `selOrganizerEditorView`).
 */
export type STSendClientOrganizerState =
  | (SendClientOrganizerSession & { status: 'loading' })
  | (SendClientOrganizerSession & { status: 'loadFailed' })
  | (SendClientOrganizerSession & EditorState & { status: 'loaded' })

/** The editor as a dialog shows it, with its parent's write progress folded in. */
export type STSendClientOrganizerView =
  | (SendClientOrganizerSession & { status: 'loading' })
  | (SendClientOrganizerSession & { status: 'loadFailed' })
  | (SendClientOrganizerSession &
      EditorState & { status: 'loaded' | 'saving' | 'savingAndClosing' | 'sending' | 'sent' })

/** The email a write persists: the draft, its persisted baseline and its purpose. */
export type OrganizerEmail = Pick<
  Extract<STSendClientOrganizerState, { status: 'loaded' }>,
  'folderId' | 'target' | 'sendState' | 'editedRecipients' | 'body'
>

export type STSendClientOrganizerMessage =
  // Show the persisted recipients, subject, and message, now or after a change.
  | { type: 'sendStateChanged'; sendState: STFolderSendState }
  // Show that the saved email draft could not be loaded.
  | { type: 'sendStateLoadFailed' }
  // Hand the draft to the parent to save.
  | { type: 'saveRequested' }
  // Hand the draft to the parent to save, closing once it is saved.
  | { type: 'saveAndCloseRequested' }
  // Hand the email to the parent to send.
  | { type: 'sendRequested'; deleteDraft?: boolean }
  // Show this text in the recipient input.
  | { type: 'emailTyped'; email: string }
  // Show this text in the message editor.
  | { type: 'bodyChanged'; body: string }
  // Highlight this recipient for keyboard editing.
  | { type: 'emailSelected'; email: string | undefined }
  // Add this email to the recipient list.
  | { type: 'emailAdded'; email: string }
  // Remove this email from the recipient list.
  | { type: 'emailRemoved'; email: string }
  // Copy the generated questionnaire URL through the managed clipboard boundary.
  | { type: 'copyQuestionnaireLinkRequested'; url: string }

/** The writes the editor asks its parent to run. */
export type STSendClientOrganizerOutput =
  | { type: 'emailSendRequested'; email: OrganizerEmail; deleteDraft?: boolean }
  | { type: 'draftSaveRequested'; email: OrganizerEmail; closeAfter: boolean }

type OrganizerUpdate = ModuleUpdate<
  STSendClientOrganizerState,
  STSendClientOrganizerMessage,
  STSendClientOrganizerOutput
>

/** The screen where the email draft is loading. */
type LoadingState = Extract<STSendClientOrganizerState, { status: 'loading' }>

/** The screen where the email is ready to edit. */
type LoadedState = Extract<STSendClientOrganizerState, { status: 'loaded' }>

type KnownRecipient = Extract<SendClientOrganizerRecipient, { type: 'knownUser' }>

type EditorEdit = Extract<
  STSendClientOrganizerMessage,
  { type: 'bodyChanged' | 'emailTyped' | 'emailAdded' | 'emailRemoved' | 'emailSelected' }
>

const observeSendState = Task.define((folderId: string, messageType: ClientOrganizerMessageType) =>
  Task.pipe(
    query({ type: 'folders/getFolderSendState', folderId, messageType }),
    Task.map((sendState) => ({ type: 'sendStateChanged', sendState }) as const),
    Task.catchTags({
      QueryClosed: () => Task.succeed({ type: 'sendStateLoadFailed' } as const)
    })
  )
)

/**
 * The persisted email is a live query, so it stays current for as long as the
 * editor is open; leaving the editor closes it.
 */
export const stSendClientOrganizerModule = defModule<{
  state: STSendClientOrganizerState
  message: STSendClientOrganizerMessage
  init: SendClientOrganizerSession
  output: STSendClientOrganizerOutput
}>({
  init: (session) => ({ ...session, status: 'loading' }),
  tasks: (state) =>
    match(state)
      .with({ status: P.union('loading', 'loaded') }, (state) => [
        observeSendState(state.folderId, clientOrganizerMessageType(state.target))
      ])
      .otherwise(() => []),
  update: transitions('status', {
    loading: {
      sendStateChanged: (state, message) => loadedPhase(state, message.sendState),
      sendStateLoadFailed: (state) => [
        { ...state, status: 'loadFailed' },
        showSnackbar('Failed to load email send state', { variant: 'error' })
      ]
    },
    loadFailed: {},
    loaded: {
      sendStateChanged: (state, message) => withPersistedEmail(state, message.sendState),
      // The subscription closed; the editor keeps the last persisted email it saw.
      ...ignore<STSendClientOrganizerMessage>('sendStateLoadFailed'),
      // Committing recipient input must use the same eligibility check as Enter.
      emailAdded: (state, message) =>
        isAssigneeEmail(state, message.email)
          ? [state, rejectAssigneeRecipient()]
          : updateEditor(state, message),
      bodyChanged: updateEditor,
      emailTyped: updateEditor,
      emailRemoved: updateEditor,
      emailSelected: updateEditor,
      copyQuestionnaireLinkRequested: (state, message) =>
        state.dialog == 'shareableLink' && isAssigneeEmail(state, state.inputValue)
          ? [state, rejectAssigneeRecipient()]
          : copyQuestionnaireLink(state, message.url),
      saveRequested: (state) => saveDraft(state, false),
      saveAndCloseRequested: (state) =>
        state.dialog == 'shareableLink' && isAssigneeEmail(state, state.inputValue)
          ? [state, rejectAssigneeRecipient()]
          : saveDraft(editorForSaveAndClose(state), true),
      sendRequested: (state, message) =>
        emit(state, { type: 'emailSendRequested', email: state, deleteDraft: message.deleteDraft })
    }
  })
})

/** Hands the draft to the parent to save, copying the shareable link if asked. */
function saveDraft(state: LoadedState, closeAfter: boolean, copiedLink?: string): OrganizerUpdate {
  const result: UpdateResult<STSendClientOrganizerState, STSendClientOrganizerMessage> =
    copiedLink == undefined
      ? state
      : [state, copyTextToClipboard(copiedLink, 'Copied shareable link to clipboard')]
  return emit(result, { type: 'draftSaveRequested', email: state, closeAfter })
}

function loadedPhase(state: LoadingState, sendState: STFolderSendState): LoadedState {
  const { status: _status, ...session } = state
  return {
    ...session,
    status: 'loaded',
    sendState,
    body: sendState.message ?? '',
    editedRecipients: sendState.recipients.map((user) => ({
      type: 'knownUser',
      user,
      email: user.email
    })),
    inputValue: '',
    selectedEmail: undefined
  }
}

// The draft is the person's; only the persisted email underneath it changes. A
// recipient added by email becomes a known user once a save creates them.
function withPersistedEmail(state: LoadedState, sendState: STFolderSendState): LoadedState {
  const known = new Map(sendState.recipients.map((user) => [user.email, user]))
  return {
    ...state,
    sendState,
    editedRecipients: state.editedRecipients.map((recipient) => {
      const user = recipient.type == 'unknownUser' ? known.get(recipient.email) : undefined
      return user == undefined ? recipient : { type: 'knownUser', user, email: user.email }
    })
  }
}

function copyQuestionnaireLink(state: LoadedState, url: string): OrganizerUpdate {
  return match(state.dialog)
    .returnType<OrganizerUpdate>()
    .with('sendEmail', () => saveDraft(state, true, url))
    .with('shareableLink', () => saveDraft(addPendingRecipient(state), false, url))
    .with(P.union('requestMissingDocuments', 'editBulkEmail'), () => {
      throw new Error(`Unexpected copy request from ${state.dialog} dialog`)
    })
    .exhaustive()
}

function addPendingRecipient(state: LoadedState): LoadedState {
  if (!isValidEmail(state.inputValue)) {
    return state
  }
  return updateEditor(state, { type: 'emailAdded', email: state.inputValue })
}

function editorForSaveAndClose(state: LoadedState): LoadedState {
  return match(state.dialog)
    .with('shareableLink', () => addPendingRecipient(state))
    .with(P.union('sendEmail', 'requestMissingDocuments', 'editBulkEmail'), () => state)
    .exhaustive()
}

function updateEditor(state: LoadedState, message: EditorEdit): LoadedState {
  return match(message)
    .returnType<LoadedState>()
    .with({ type: 'bodyChanged' }, ({ body }) => ({ ...state, body }))
    .with({ type: 'emailTyped' }, ({ email }) => ({ ...state, inputValue: email }))
    .with({ type: 'emailAdded' }, ({ email }) => {
      const existingUser = state.sendState.recipients.find((recipient) => recipient.email == email)
      const recipient: SendClientOrganizerRecipient = match(existingUser)
        .with(P.nonNullable, (user) => ({
          type: 'knownUser' as const,
          user,
          email: user.email
        }))
        .with(P.nullish, () => ({ type: 'unknownUser' as const, email }))
        .exhaustive()
      return {
        ...state,
        editedRecipients: [...state.editedRecipients, recipient],
        inputValue: '',
        selectedEmail: undefined
      }
    })
    .with({ type: 'emailRemoved' }, ({ email }) => ({
      ...state,
      editedRecipients: state.editedRecipients.filter((recipient) => recipient.email != email),
      selectedEmail: undefined,
      inputValue: ''
    }))
    .with({ type: 'emailSelected' }, ({ email }) => ({ ...state, selectedEmail: email }))
    .exhaustive()
}

/** Identifies one email draft: a folder's email of one kind. */
export function organizerDraft(email: { folderId: string; target: SendClientOrganizerTarget }) {
  return `${email.folderId}:${clientOrganizerMessageType(email.target)}`
}

/**
 * The write a parent is running, or has just finished, for one draft. Its
 * outcome is a fact about the draft, so it lands here whether or not a dialog
 * for that draft is still open, and a dialog opened later shows it.
 */
export type OrganizerWrite =
  | { status: 'saving'; closeAfter: boolean }
  | { status: 'sending' }
  | { status: 'sent' }

export type OrganizerWrites = Readonly<Record<string, OrganizerWrite>>

/** How a write for one draft ended; the user has already been told. */
export type OrganizerWriteFinished = {
  type: 'organizerWriteFinished'
  draft: string
  outcome: 'saved' | 'sent' | 'failed'
}

/**
 * Starts the write the editor asked for, recording it under its draft. One
 * write per draft: while one runs, another request has nothing to start.
 */
export function startOrganizerWrite<State extends { organizerWrites: OrganizerWrites }>(
  state: State,
  output: STSendClientOrganizerOutput,
  write: Effect.Effect<OrganizerWriteFinished['outcome'], never, any> = organizerWrite(output)
): UpdateResult<State, OrganizerWriteFinished> {
  const draft = organizerDraft(output.email)
  const running = state.organizerWrites[draft]
  if (running != undefined && running.status != 'sent') return state
  const started: OrganizerWrite = match(output)
    .returnType<OrganizerWrite>()
    .with({ type: 'draftSaveRequested' }, ({ closeAfter }) => ({ status: 'saving', closeAfter }))
    .with({ type: 'emailSendRequested' }, () => ({ status: 'sending' }))
    .exhaustive()
  return [
    { ...state, organizerWrites: { ...state.organizerWrites, [draft]: started } },
    write.pipe(
      Effect.map((outcome) => ({ type: 'organizerWriteFinished', draft, outcome }) as const)
    )
  ]
}

/** Records how a write ended; a sent email stays recorded so its dialog can say so. */
export function finishOrganizerWrite(
  writes: OrganizerWrites,
  { draft, outcome }: OrganizerWriteFinished
): OrganizerWrites {
  const { [draft]: _finished, ...rest } = writes
  return outcome == 'sent' ? { ...rest, [draft]: { status: 'sent' } } : rest
}

/**
 * Opening a draft's editor starts over: an earlier send's confirmation is
 * dropped, and a save that was to close the previous dialog no longer closes
 * this one.
 */
export function reopenOrganizerDraft(writes: OrganizerWrites, draft: string): OrganizerWrites {
  const write = writes[draft]
  if (write == undefined) return writes
  if (write.status == 'sent') {
    const { [draft]: _sent, ...rest } = writes
    return rest
  }
  if (write.status == 'saving')
    return { ...writes, [draft]: { status: 'saving', closeAfter: false } }
  return writes
}

/** Whether a finished write was the save its dialog was waiting on to close. */
export function closesAfterSave(
  write: OrganizerWrite | undefined,
  finished: OrganizerWriteFinished
) {
  return finished.outcome == 'saved' && write?.status == 'saving' && write.closeAfter
}

export function selOrganizerEditorView(
  editor: STSendClientOrganizerState,
  write: OrganizerWrite | undefined
): STSendClientOrganizerView {
  if (editor.status != 'loaded' || write == undefined) return editor
  return {
    ...editor,
    status: match(write)
      .with({ status: 'saving', closeAfter: true }, () => 'savingAndClosing' as const)
      .with({ status: 'saving' }, () => 'saving' as const)
      .with({ status: 'sending' }, () => 'sending' as const)
      .with({ status: 'sent' }, () => 'sent' as const)
      .exhaustive()
  }
}

function organizerWrite(output: STSendClientOrganizerOutput) {
  return match(output)
    .with({ type: 'draftSaveRequested' }, ({ email }) => saveOrganizerDraft(email))
    .with({ type: 'emailSendRequested' }, ({ email, deleteDraft }) =>
      sendOrganizerEmail(email, deleteDraft)
    )
    .exhaustive()
}

const email = child(stSendClientOrganizerModule, 'email')

const observeOrganizerRoute = observeRoute(P._)

/**
 * The organizer email dialog outside a binder. It opens and closes the editor,
 * and runs the writes the editor hands it, recording each under its draft.
 */
export type STSendClientOrganizerDialogState =
  | { phase: 'closed'; organizerWrites: OrganizerWrites }
  | { phase: 'open'; organizerWrites: OrganizerWrites; email: STSendClientOrganizerState }

export type STSendClientOrganizerDialogMessage =
  // Open the email dialog and load its saved draft.
  | ({ type: 'opened' } & SendClientOrganizerSession)
  // Close the email dialog.
  | { type: 'closed' }
  // A message for the editor.
  | { type: 'email'; message: STSendClientOrganizerMessage }
  | OrganizerWriteFinished
  | TaskMessage<typeof observeOrganizerRoute>
  | TaskMessage<typeof observeCurrentUser>

type DialogUpdate = UpdateResult<
  STSendClientOrganizerDialogState,
  STSendClientOrganizerDialogMessage
>
type OpenDialogState = Extract<STSendClientOrganizerDialogState, { phase: 'open' }>

export const stSendClientOrganizerDialogModule = defModule<{
  state: STSendClientOrganizerDialogState
  message: STSendClientOrganizerDialogMessage
}>({
  init: () => ({ phase: 'closed', organizerWrites: {} }),
  tasks: (state) => [
    observeOrganizerRoute(),
    observeCurrentUser(),
    ...match(state)
      .with({ phase: 'open' }, (state) => email.tasks(state.email))
      .otherwise(() => [])
  ],
  update: transitions('phase', {
    closed: {
      opened: openDialog,
      organizerWriteFinished: (state, finished) => ({
        ...state,
        organizerWrites: finishOrganizerWrite(state.organizerWrites, finished)
      }),
      // A closed dialog has nothing to close.
      ...ignore<STSendClientOrganizerDialogMessage>('routeChanged', 'currentUserChanged')
    },
    open: {
      opened: openDialog,
      closed: closeDialog,
      routeChanged: closeDialog,
      currentUserChanged: closeDialog,
      email: (state, { message }) =>
        email.update(state.email, message, (email) => ({ ...state, email }), {
          emailSendRequested: (output, state) => startOrganizerWrite(state, output),
          draftSaveRequested: (output, state) => startOrganizerWrite(state, output)
        }),
      organizerWriteFinished: (state, finished) => {
        const next = {
          ...state,
          organizerWrites: finishOrganizerWrite(state.organizerWrites, finished)
        }
        const closes =
          organizerDraft(state.email) == finished.draft &&
          (closesAfterSave(state.organizerWrites[finished.draft], finished) ||
            (finished.outcome == 'sent' && closeAfterSend(state.email.target)))
        return closes ? closeDialog(next) : next
      }
    }
  })
})

function openDialog(
  state: STSendClientOrganizerDialogState,
  { type: _type, ...session }: Extract<STSendClientOrganizerDialogMessage, { type: 'opened' }>
): DialogUpdate {
  return Update.mapState(email.init(session), (email) => ({
    phase: 'open' as const,
    organizerWrites: reopenOrganizerDraft(state.organizerWrites, organizerDraft(session)),
    email
  }))
}

function closeDialog(state: OpenDialogState): STSendClientOrganizerDialogState {
  return { phase: 'closed', organizerWrites: state.organizerWrites }
}

/** The open editor as its dialog shows it. */
export function selOpenOrganizerEditor(state: OpenDialogState) {
  return selOrganizerEditorView(state.email, state.organizerWrites[organizerDraft(state.email)])
}
export function sendClientOrganizer({
  deleteDraft
}: { deleteDraft?: boolean } = {}): STSendClientOrganizerMessage {
  return { type: 'sendRequested', deleteDraft }
}

export function openSendClientOrganizerDialog(
  args: { folderId: string; users: BasicUser[] } & SendClientOrganizerTarget
): STSendClientOrganizerDialogMessage {
  const { folderId, users, ...target } = args
  return { type: 'opened', folderId, users, dialog: 'sendEmail', target }
}

export function openShareableLinkDialog(
  folderId: string,
  users: BasicUser[]
): STSendClientOrganizerDialogMessage {
  return {
    type: 'opened',
    users,
    dialog: 'shareableLink',
    folderId,
    target: {
      type: 'clientOrganizerEmail',
      messageType: 'client_organizer_invitation'
    }
  }
}

export function openMissingDocumentsDialog(
  folderId: string,
  users: BasicUser[]
): STSendClientOrganizerDialogMessage {
  return {
    type: 'opened',
    users,
    dialog: 'requestMissingDocuments',
    folderId,
    target: {
      type: 'clientOrganizerEmail',
      messageType: 'client_organizer_document_request'
    }
  }
}

export function openBulkEmailEditor(
  folderId: string,
  users: BasicUser[]
): STSendClientOrganizerDialogMessage {
  return {
    type: 'opened',
    users,
    dialog: 'editBulkEmail',
    folderId,
    target: {
      type: 'clientOrganizerEmail',
      messageType: 'client_organizer_invitation'
    }
  }
}

export function clientOrganizerMessageType(
  target: SendClientOrganizerTarget
): ClientOrganizerMessageType {
  return match(target)
    .with({ type: 'clientOrganizerEmail' }, ({ messageType }) => messageType)
    .with({ type: 'taxReturnDelivery' }, () => 'tax_return_delivery' as const)
    .exhaustive()
}

export function closeSendClientOrganizerDialog(): STSendClientOrganizerDialogMessage {
  return { type: 'closed' }
}

// The dialog receives existing organization users; assignment is checked by stable user ID.
function isAssigneeEmail(state: SendClientOrganizerSession & EditorState, email: string): boolean {
  const user = state.users.find((user) => user.email.toLowerCase() == email.toLowerCase())
  return user != undefined && state.sendState.folder.assigneeIds.includes(user.id)
}

function rejectAssigneeRecipient() {
  return showSnackbar('Remove their assignment before adding them as a client.', {
    variant: 'error'
  })
}

export function selCanSendClientOrganizer(state: STSendClientOrganizerView) {
  return state.status == 'loaded' && state.editedRecipients.length > 0
}

export function saveClientOrganizerChanges(): STSendClientOrganizerMessage {
  return { type: 'saveRequested' }
}

export function saveAndCloseClientOrganizer(): STSendClientOrganizerMessage {
  return { type: 'saveAndCloseRequested' }
}

/**
 * Persists the draft, publishes a delivered return, and sends the email. The
 * user hears the outcome here, so it reaches them even after the dialog closed;
 * a send that reaches nobody, such as over the free quota, counts as failed.
 */
export function sendOrganizerEmail(email: OrganizerEmail, deleteDraft?: boolean) {
  const send = Effect.gen(function* () {
    yield* persistOrganizerChanges(email)
    const sdk = yield* SdkClient
    yield* match(email.target)
      .with({ type: 'taxReturnDelivery' }, ({ contractId }) =>
        sdk.send({ type: 'folders/publishContract', folderId: email.folderId, contractId })
      )
      .with({ type: 'clientOrganizerEmail' }, () => Effect.void)
      .exhaustive()
    const response = yield* sdk.send({
      type: 'folders/sendClientOrganizerEmail',
      folderId: email.folderId,
      messageType: clientOrganizerMessageType(email.target),
      deleteDraft
    })
    return response
  })

  return Effect.gen(function* () {
    const result = yield* runSdkRequest(send)
    if (!result.ok) {
      yield* Effect.logError('Failed to send organizer email', result.error)
      yield* showSnackbar('Failed to send email', { variant: 'error' })
      return 'failed' as const
    }
    return yield* match(result.response)
      .with({ type: 'ok' }, () => showSnackbar('Sent email').pipe(Effect.as('sent' as const)))
      .with({ type: 'quota_exceeded', quota: P.number }, ({ quota }) =>
        showSnackbar(
          `You have exceeded the maximum quota of ${quota} emails for a free account. Please upgrade to remove this restriction.`,
          { variant: 'error' }
        ).pipe(Effect.as('failed' as const))
      )
      .run()
  })
}

/** Persists the draft; a failure is reported here, even after the dialog closed. */
export function saveOrganizerDraft(email: OrganizerEmail) {
  return Effect.gen(function* () {
    const result = yield* runSdkRequest(persistOrganizerChanges(email))
    if (result.ok) return 'saved' as const
    yield* Effect.logError('Failed to save organizer draft', result.error)
    yield* showSnackbar('Failed to save draft', { variant: 'error' })
    return 'failed' as const
  })
}

function persistOrganizerChanges(state: OrganizerEmail) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const recipients = yield* resolveUnknownRecipients(state.editedRecipients)

    const updateAccessRequest = folderAccessUpdateRequest({
      folderId: state.folderId,
      previousRecipients: state.sendState.recipients,
      nextRecipients: recipients
    })
    if (updateAccessRequest) {
      yield* sdk.send(updateAccessRequest)
    }

    if (state.sendState.message != state.body) {
      yield* sdk.send({
        type: 'folders/saveFolderDraftMessage',
        folderId: state.folderId,
        messageType: clientOrganizerMessageType(state.target),
        message: state.body
      })
    }

    return recipients
  })
}

function showSnackbar(message: string, options?: { variant: 'error' }) {
  return Effect.gen(function* () {
    const snackbar = yield* Snackbar
    yield* snackbar.show(message, options)
    return null
  })
}

/** Whether the dialog closes once its email is sent, rather than showing it as sent. */
function closeAfterSend(target: SendClientOrganizerTarget) {
  return match(clientOrganizerMessageType(target))
    .with(P.union('client_organizer_document_request', 'tax_return_delivery'), () => true)
    .with(P.union('client_organizer_invitation', 'client_organizer_submit_reminder'), () => false)
    .exhaustive()
}

function resolveUnknownRecipients(recipients: SendClientOrganizerRecipient[]) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const unknownEmails = recipients
      .filter((recipient) => recipient.type == 'unknownUser')
      .map((recipient) => recipient.email)

    if (unknownEmails.length == 0) {
      return recipients
    }

    const response = yield* sdk.send({
      type: 'accounts/getOrCreateUsersByEmail',
      emails: unknownEmails
    })

    return recipients.map((recipient) =>
      match(recipient)
        .returnType<SendClientOrganizerRecipient>()
        .with({ type: 'knownUser' }, (recipient) => recipient)
        .with({ type: 'unknownUser' }, (recipient) => {
          const user = response.users.find(
            (candidate) => candidate.email.toLowerCase() == recipient.email.toLowerCase()
          )
          if (!user) {
            throw new Error(`No user returned for organizer recipient: ${recipient.email}`)
          }
          return {
            type: 'knownUser',
            user,
            email: user.email
          }
        })
        .exhaustive()
    )
  })
}

/**
 * Unknown recipients have no role identity yet. Only known users participate
 * in the access diff; resolution happens before this boundary.
 */
function folderAccessUpdateRequest({
  folderId,
  previousRecipients,
  nextRecipients
}: {
  folderId: string
  previousRecipients: BasicUser[]
  nextRecipients: SendClientOrganizerRecipient[]
}): UpdateFolderAccess | undefined {
  const previousUserIds = previousRecipients.map((recipient) => recipient.id)
  const nextUserIds = nextRecipients
    .filter((recipient): recipient is KnownRecipient => recipient.type == 'knownUser')
    .map((recipient) => recipient.user.id)

  const removeRequests: FolderAccessRequest[] = previousUserIds
    .filter((userId) => !nextUserIds.includes(userId))
    .map((userId) => ({
      type: 'folders/updateFolderAccess',
      provider: 'local',
      uid: userId,
      role: 'revoke'
    }))

  const addRequests: FolderAccessRequest[] = nextUserIds
    .filter((userId) => !previousUserIds.includes(userId))
    .map((userId) => ({
      type: 'folders/updateFolderAccess',
      provider: 'local',
      uid: userId,
      role: 'client'
    }))

  const request: UpdateFolderAccess = {
    type: 'folders/updateFolderAccess',
    folderId,
    requests: [...removeRequests, ...addRequests]
  }

  if (request.requests.length > 0) {
    return request
  }
  return undefined
}
